import { ReceiptText, Users, Wallet, Coins } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { Skeleton } from "@/core/components/ui/skeleton";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { usePayrollBatchStats } from "@/features/payroll/api/use-payroll-batch-stats";
import { AmountMinor } from "@/features/shared/components/amount-minor";

/**
 * Cartes d'agrégat du tableau de bord payroll (`GET /PayrollBatches/stats`) —
 * nombre de paies, paies payées, salariés payés, masse salariale brute, frais,
 * total débité sur la période (filtres `from`/`to` à ajouter en étape UI
 * ultérieure). Posées en haut de la page liste.
 */
export function PayrollStatsCards() {
  const { t } = useTypedTranslation();
  const { data, isLoading } = usePayrollBatchStats();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
        <Skeleton className="h-28" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("payroll.stats.batchCount")}
          </CardTitle>
          <ReceiptText className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <NoTranslate as="span" className="block text-2xl font-semibold">
            {data.batchCount}
          </NoTranslate>
          <p className="text-xs text-muted-foreground">
            {t("payroll.stats.paidBatchCount")} :{" "}
            <NoTranslate as="span">{data.paidBatchCount}</NoTranslate>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("payroll.stats.employeesPaid")}
          </CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <NoTranslate as="span" className="block text-2xl font-semibold">
            {data.employeesPaid}
          </NoTranslate>
          {(data.memberEmployeesPaid != null ||
            data.nonMemberEmployeesPaid != null) && (
            <p className="text-xs text-muted-foreground">
              {t("payroll.stats.employeesSplit")} :{" "}
              <NoTranslate as="span">
                {data.memberEmployeesPaid ?? 0}
              </NoTranslate>
              {" / "}
              <NoTranslate as="span">
                {data.nonMemberEmployeesPaid ?? 0}
              </NoTranslate>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("payroll.stats.grossAmount")}
          </CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent className="space-y-1">
          <AmountMinor
            amountMinor={data.grossAmountMinor}
            currency={data.currency}
            className="block text-2xl font-semibold"
          />
          <p className="text-xs text-muted-foreground">
            {t("payroll.stats.feeAmount")} :{" "}
            <AmountMinor
              amountMinor={data.feeAmountMinor}
              currency={data.currency}
            />
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("payroll.stats.debitAmount")}
          </CardTitle>
          <Coins className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <AmountMinor
            amountMinor={data.debitAmountMinor}
            currency={data.currency}
            className="block text-2xl font-semibold"
          />
        </CardContent>
      </Card>
    </div>
  );
}
